import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';

const STATUS_BADGE = {
  draft: { label: 'Draft', bg: '#eef2ff', color: '#3730a3' },
  sent: { label: 'Sent', bg: '#dcfce7', color: '#166534' },
  discarded: { label: 'Discarded', bg: '#f3f4f6', color: '#6b7280' },
};

const sendCallable = httpsCallable(functions, 'sendPortalWelcomeEmail');
const updateCallable = httpsCallable(functions, 'updatePortalWelcomeEmail');
const discardCallable = httpsCallable(functions, 'discardPortalWelcomeEmail');
const regenerateCallable = httpsCallable(functions, 'regeneratePortalWelcomeEmail');

const PortalEmails = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('draft');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');

  // Local edit state (only applied on Save)
  const [editSubject, setEditSubject] = useState('');
  const [editGreeting, setEditGreeting] = useState('');
  const [editIntro, setEditIntro] = useState('');
  const [editSignature, setEditSignature] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [editTo, setEditTo] = useState('');
  const [editCc, setEditCc] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'portalEmailDrafts'), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDrafts(rows);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load drafts');
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(
    () => drafts.filter((d) => (statusFilter === 'all' ? true : d.status === statusFilter)),
    [drafts, statusFilter],
  );

  const selected = useMemo(
    () => drafts.find((d) => d.id === selectedId) || null,
    [drafts, selectedId],
  );

  useEffect(() => {
    if (!selected) {
      setEditSubject('');
      setEditGreeting('');
      setEditIntro('');
      setEditSignature('');
      setEditHtml('');
      setEditTo('');
      setEditCc('');
      setDirty(false);
      return;
    }
    if (!dirty) {
      setEditSubject(selected.subject || '');
      setEditGreeting(selected.greetingName ?? '');
      setEditIntro(selected.customIntro ?? '');
      setEditSignature(selected.customSignature ?? '');
      setEditHtml(selected.htmlBody || '');
      setEditTo(selected.to || '');
      setEditCc((selected.cc || []).join(', '));
    }
  }, [selected, dirty]);

  const doSave = async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      await updateCallable({
        draftId: selected.id,
        patch: {
          subject: editSubject,
          cc: editCc.split(',').map((s) => s.trim()).filter(Boolean),
          greetingName: editGreeting,
          customIntro: editIntro,
          customSignature: editSignature,
        },
      });
      setDirty(false);
      setBanner('Draft saved.');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmAndDo = async (msg, fn) => {
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const doSend = () => confirmAndDo(
    `Send this email to ${editTo}? This cannot be undone.`,
    async () => {
      if (dirty) await doSave();
      await sendCallable({ draftId: selected.id });
      setBanner('Sent.');
    },
  );

  const doDiscard = () => confirmAndDo(
    'Discard this draft?',
    async () => {
      await discardCallable({ draftId: selected.id });
      setSelectedId(null);
      setBanner('Discarded.');
    },
  );

  const doRegenerate = () => confirmAndDo(
    'Regenerate from template? Any unsent edits will be lost.',
    async () => {
      await regenerateCallable({ draftId: selected.id });
      setDirty(false);
      setBanner('Regenerated from template.');
    },
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <h1 style={{ margin: '0 0 0.5rem' }}>Portal welcome emails</h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Review, edit, and send the portal invitation email for each client.
      </p>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
      {banner && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{banner}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '1rem', minHeight: '70vh' }}>
        {/* Left: draft list */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem' }}>
            {['draft', 'sent', 'discarded', 'all'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`tab-button ${statusFilter === s ? 'active' : ''}`}
                style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
              >
                {s}
              </button>
            ))}
          </div>
          {loading ? (
            <div style={{ padding: '1rem', color: '#6b7280' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '1rem', color: '#6b7280' }}>No {statusFilter} drafts.</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '70vh', overflowY: 'auto' }}>
              {filtered.map((d) => {
                const s = STATUS_BADGE[d.status] || STATUS_BADGE.draft;
                const active = d.id === selectedId;
                return (
                  <li
                    key={d.id}
                    onClick={() => { setSelectedId(d.id); setDirty(false); setBanner(''); }}
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      background: active ? '#f0f7ff' : 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <strong style={{ fontSize: '0.95rem' }}>{d.clientName || d.clientEmail}</strong>
                      <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600 }}>{s.label}</span>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 2 }}>{d.clientEmail}</div>
                    {d.monthlyRetainer > 0 && (
                      <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: 2 }}>
                        {d.currency} {Number(d.monthlyRetainer).toLocaleString()}/mo
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Right: editor + preview */}
        <div className="card" style={{ padding: '1rem' }}>
          {!selected ? (
            <div style={{ color: '#6b7280' }}>Select a draft on the left to preview or edit.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <button className="btn btn-primary" onClick={doSend} disabled={busy || selected.status !== 'draft'}>Send</button>
                <button className="btn" onClick={doSave} disabled={busy || !dirty || selected.status !== 'draft'}>Save</button>
                <button className="btn" onClick={doRegenerate} disabled={busy || selected.status !== 'draft'}>Regenerate from template</button>
                <button className="btn" onClick={doDiscard} disabled={busy || selected.status !== 'draft'} style={{ marginLeft: 'auto', color: '#b91c1c' }}>Discard</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                {/* Left: simple editor */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>To</label>
                    <input value={editTo} disabled readOnly />
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                      Locked — edit client record to change
                    </div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>CC</label>
                    <input
                      value={editCc}
                      onChange={(e) => { setEditCc(e.target.value); setDirty(true); }}
                      disabled={selected.status !== 'draft'}
                      placeholder="email1@example.com, email2@example.com"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Subject</label>
                    <input
                      value={editSubject}
                      onChange={(e) => { setEditSubject(e.target.value); setDirty(true); }}
                      disabled={selected.status !== 'draft'}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Greeting name</label>
                    <input
                      value={editGreeting}
                      onChange={(e) => { setEditGreeting(e.target.value); setDirty(true); }}
                      disabled={selected.status !== 'draft'}
                      placeholder="e.g. Fatima"
                    />
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                      Used in "Hi [name],"
                    </div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Message</label>
                    <textarea
                      value={editIntro}
                      onChange={(e) => { setEditIntro(e.target.value); setDirty(true); }}
                      disabled={selected.status !== 'draft'}
                      rows={5}
                      style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.9rem' }}
                      placeholder="Opening paragraph of the email…"
                    />
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                      Goes after "Hi [name]," — before the portal button
                    </div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Signature</label>
                    <textarea
                      value={editSignature}
                      onChange={(e) => { setEditSignature(e.target.value); setDirty(true); }}
                      disabled={selected.status !== 'draft'}
                      rows={3}
                      style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.9rem' }}
                      placeholder="Best regards,&#10;The FusionICT Team"
                    />
                  </div>
                </div>

                {/* Right: rendered preview — updates after Save */}
                <div>
                  <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '0.4rem' }}>Preview (updates after Save)</div>
                  <iframe
                    key={selectedId + '_' + editHtml.length}
                    title="Preview"
                    srcDoc={editHtml || '<p style="font-family:sans-serif;color:#6b7280;padding:1rem">No content — try Regenerate from template.</p>'}
                    style={{ width: '100%', minHeight: '480px', border: '1px solid #e5e7eb', borderRadius: '4px', background: '#fff' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>

              {selected.sentAt && (
                <div style={{ marginTop: '1rem', color: '#6b7280', fontSize: '0.85rem' }}>
                  Sent {selected.sentAt.toDate?.().toLocaleString?.() || String(selected.sentAt)} — message ID: {selected.sentMessageId}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalEmails;
